<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i>
                新增規則
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="info-banner">
      <i class="ri-information-line"></i>
      <div class="info-content">
        <strong>優先級說明：</strong>
        當有效的時間區間給點規則<span class="highlight">進行中</span>時，該規則將取代「常態給點規則」設定，對期間內的訂單套用時間區間的回饋率。時間區間規則結束後，系統自動回復常態給點規則。
      </div>
    </div>

    <div v-if="activeRuleLoading" class="active-rule-loading">
      <span class="spinner spinner-sm"></span>
      載入生效規則中...
    </div>

    <div v-else-if="activeTimeRangeRule" class="active-rule-card">
      <div class="active-rule-header">
        <div class="active-rule-title-row">
          <span class="active-pulse-dot"></span>
          <h2 class="active-rule-title">目前生效中的時間區間給點規則</h2>
          <span :class="getStatusBadgeClass(activeTimeRangeRule.status)">
            {{ getStatusText(activeTimeRangeRule.status) }}
          </span>
        </div>
        <div class="active-rule-actions">
          <button
            type="button"
            class="btn btn-sm btn-secondary"
            @click="handleEdit(activeTimeRangeRule.id)"
          >
            <i class="ri-edit-line"></i>
            編輯規則
          </button>
        </div>
      </div>

      <div class="active-rule-body">
        <div class="active-rule-main">
          <div class="active-rule-name">{{ activeTimeRangeRule.ruleName }}</div>
          <div class="active-rule-period">
            <i class="ri-time-line"></i>
            {{ formatDateTime(activeTimeRangeRule.startTime) }}
            <span class="period-sep">～</span>
            {{ formatDateTime(activeTimeRangeRule.endTime) }}
          </div>
          <p class="active-rule-hint">
            期間內的訂單將套用此規則的回饋率與給點計算設定，取代常態給點規則。
          </p>
        </div>

        <div class="active-rule-details">
          <div class="active-detail-block">
            <div class="active-detail-label">會員等級回饋率</div>
            <div class="tier-config-preview">
              <span
                v-for="(rate, tier) in activeTimeRangeRule.tierConfig"
                :key="tier"
                class="tier-rate-badge tier-rate-badge--active"
              >
                {{ getTierName(tier) }}: {{ rate }}%
              </span>
              <span v-if="!hasTierRates(activeTimeRangeRule.tierConfig)" class="active-detail-empty">
                沿用常態規則回饋率
              </span>
            </div>
          </div>

          <div class="active-detail-block">
            <div class="active-detail-label">給點計算設定</div>
            <div v-if="activeTimeRangeRule.useNormalCalculationSettings" class="active-calc-summary">
              <span class="calc-mode-tag calc-mode-tag--normal">沿用常態給點規則</span>
            </div>
            <div v-else class="active-calc-summary">
              <span class="calc-mode-tag calc-mode-tag--custom">自訂給點規則</span>
              <span>金額基數：每 {{ activeTimeRangeRule.baseAmount || 1 }} 元</span>
              <span>捨入方式：{{ getRoundingMethodLabel(activeTimeRangeRule.roundingMethod) }}</span>
              <span>生效天數：{{ activeTimeRangeRule.effectiveDays || 0 }} 天</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="no-active-rule-banner">
      <i class="ri-checkbox-circle-line"></i>
      <span>目前無進行中的時間區間給點規則，訂單套用<strong>常態給點規則</strong>。</span>
      <router-link to="/client/points/earning-rules/normal" class="no-active-rule-link">
        查看常態給點規則
      </router-link>
    </div>

    <div class="page-toolbar">
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="draft">草稿</option>
          <option value="scheduled">已排程</option>
          <option value="active">進行中</option>
          <option value="ended">已結束</option>
          <option value="disabled">已停用</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchRules">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>規則名稱</th>
            <th>開始時間</th>
            <th>結束時間</th>
            <th>狀態</th>
            <th>回饋率設定</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="rule in rules"
            :key="rule.id"
            :class="{ 'row-active-rule': isCurrentlyApplyingRule(rule) }"
          >
            <td>
              <strong>{{ rule.ruleName }}</strong>
              <span v-if="isCurrentlyApplyingRule(rule)" class="applying-badge">
                <i class="ri-flash-fill"></i>
                目前生效
              </span>
            </td>
            <td>{{ formatDateTime(rule.startTime) }}</td>
            <td>{{ formatDateTime(rule.endTime) }}</td>
            <td>
              <span :class="getStatusBadgeClass(rule.status)">
                {{ getStatusText(rule.status) }}
              </span>
            </td>
            <td>
              <div class="tier-config-preview">
                <span v-for="(rate, tier) in rule.tierConfig" :key="tier" class="tier-rate-badge">
                  {{ getTierName(tier) }}: {{ rate }}%
                </span>
              </div>
            </td>
            <td>{{ formatDateTime(rule.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button class="btn btn-sm btn-secondary" @click="handleEdit(rule.id)">
                  <i class="ri-edit-line"></i>
                  編輯
                </button>
                <button 
                  v-if="rule.status === 'active' || rule.status === 'scheduled'"
                  class="btn btn-sm btn-warning" 
                  @click="handleDisable(rule.id)"
                >
                  <i class="ri-pause-line"></i>
                  停用
                </button>
                <button 
                  v-else-if="rule.status === 'draft' || rule.status === 'disabled'"
                  class="btn btn-sm btn-success" 
                  @click="handleEnable(rule.id)"
                >
                  <i class="ri-play-line"></i>
                  啟用
                </button>
                <button class="btn btn-sm btn-info" @click="handleViewLogs(rule.id)">
                  <i class="ri-file-list-line"></i>
                  記錄
                </button>
                <button class="btn btn-sm btn-danger" @click="handleDelete(rule.id)">
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="handlePageChange"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pointsEarningRulesService } from '@/api/modules/pointsEarningRules.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { showError, showDeleteConfirm, showSuccess } from '@/utils/swal.js';

export default {
  name: 'PointsEarningRulesTimeRange',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('時間區間給點規則');
    const rules = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const filterStatus = ref('');
    const pagination = ref({
      page: 1,
      pageSize: 20,
      totalItems: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    });
    const tiers = ref([]);
    const activeRule = ref(null);
    const activeRuleLoading = ref(false);

    const activeTimeRangeRule = computed(() => {
      if (!activeRule.value || activeRule.value.ruleType !== 'time_range') {
        return null;
      }
      return activeRule.value;
    });

    const fetchActiveRule = async () => {
      activeRuleLoading.value = true;
      try {
        const response = await pointsEarningRulesService.getActiveRule();
        if (response && response.success) {
          activeRule.value = response.data || null;
        }
      } catch (err) {
        console.error('獲取生效規則失敗:', err);
      } finally {
        activeRuleLoading.value = false;
      }
    };

    const fetchTiers = async () => {
      try {
        const response = await memberTierService.getTiers({ isActive: true });
        if (response && response.success && response.data) {
          tiers.value = response.data;
        }
      } catch (err) {
        console.error('獲取會員等級列表失敗:', err);
      }
    };

    const fetchRules = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: pagination.value.page,
          pageSize: pagination.value.pageSize
        };
        if (filterStatus.value) {
          params.status = filterStatus.value;
        }

        const response = await pointsEarningRulesService.listTimeRangeRules(params);

        if (response && response.success && response.data) {
          rules.value = response.data.items || [];
          pagination.value = response.data.pagination || pagination.value;
        } else {
          error.value = response?.message || '獲取規則列表失敗';
        }
      } catch (err) {
        console.error('獲取規則列表失敗:', err);
        error.value = err.message || '獲取規則列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleFilterChange = () => {
      pagination.value.page = 1;
      fetchRules();
    };

    const handlePageChange = (page) => {
      pagination.value.page = page;
      fetchRules();
    };

    const handleCreate = () => {
      router.push('/client/points/earning-rules/time-range/create');
    };

    const handleEdit = (id) => {
      router.push(`/client/points/earning-rules/time-range/${id}/edit`);
    };

    const handleEnable = async (id) => {
      try {
        const response = await pointsEarningRulesService.enableTimeRangeRule(id);
        if (response && response.success) {
          await showSuccess('啟用成功', '規則已啟用');
          await fetchRules();
          await fetchActiveRule();
        } else {
          throw new Error(response?.message || '啟用失敗');
        }
      } catch (err) {
        console.error('啟用規則失敗:', err);
        await showError('啟用失敗', err.response?.data?.message || err.message || '啟用規則時發生錯誤');
      }
    };

    const handleDisable = async (id) => {
      const confirmed = await showDeleteConfirm('此規則');
      if (!confirmed) return;

      try {
        const response = await pointsEarningRulesService.disableTimeRangeRule(id);
        if (response && response.success) {
          await showSuccess('停用成功', '規則已停用');
          await fetchRules();
          await fetchActiveRule();
        } else {
          throw new Error(response?.message || '停用失敗');
        }
      } catch (err) {
        console.error('停用規則失敗:', err);
        await showError('停用失敗', err.response?.data?.message || err.message || '停用規則時發生錯誤');
      }
    };

    const handleDelete = async (id) => {
      const rule = rules.value.find(r => r.id === id);
      const ruleName = rule ? rule.ruleName : '此規則';
      
      // 顯示刪除確認，明確說明會連同變更記錄一起刪除
      const confirmed = await showDeleteConfirm(
        ruleName,
        `確定要刪除「${ruleName}」嗎？<br><br><div style="text-align: center; margin: 12px 0;"><strong>將同時刪除以下項目：</strong></div><ul style="text-align: left; margin: 12px auto; padding-left: 24px; display: inline-block; min-width: 200px;"><li style="margin: 6px 0;">規則本身</li><li style="margin: 6px 0;">所有變更記錄</li></ul><div style="text-align: center; margin-top: 12px;"><strong>此操作無法復原！</strong></div>`
      );

      if (!confirmed) return;

      try {
        const response = await pointsEarningRulesService.deleteTimeRangeRule(id);
        if (response && response.success) {
          await showSuccess('刪除成功', '規則及所有變更記錄已刪除');
          await fetchRules();
          await fetchActiveRule();
        } else {
          throw new Error(response?.message || '刪除失敗');
        }
      } catch (err) {
        console.error('刪除規則失敗:', err);
        await showError('刪除失敗', err.response?.data?.message || err.message || '刪除規則時發生錯誤');
      }
    };

    const handleViewLogs = (id) => {
      router.push(`/client/points/earning-rules/time-range/${id}/logs`);
    };

    const getStatusBadgeClass = (status) => {
      const classes = {
        draft: 'badge badge-secondary',
        scheduled: 'badge badge-info',
        active: 'badge badge-success',
        ended: 'badge badge-secondary',
        disabled: 'badge badge-warning'
      };
      return classes[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const texts = {
        draft: '草稿',
        scheduled: '已排程',
        active: '進行中',
        ended: '已結束',
        disabled: '已停用'
      };
      return texts[status] || status;
    };

    const getTierName = (tierCode) => {
      const tier = tiers.value.find(t => t.tierCode === tierCode);
      return tier ? tier.tierName : tierCode;
    };

    const getRoundingMethodLabel = (method) => {
      const map = { round: '四捨五入', ceil: '無條件進位', floor: '無條件捨去' };
      return map[method] || '無條件捨去';
    };

    const hasTierRates = (tierConfig) => {
      if (!tierConfig || typeof tierConfig !== 'object') return false;
      return Object.values(tierConfig).some(
        (rate) => rate !== null && rate !== undefined && rate !== ''
      );
    };

    const isCurrentlyApplyingRule = (rule) => {
      return activeTimeRangeRule.value && activeTimeRangeRule.value.id === rule.id;
    };

    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    onMounted(async () => {
      await fetchTiers();
      await fetchActiveRule();
      await fetchRules();
    });

    return {
      pageTitle,
      rules,
      loading,
      error,
      filterStatus,
      pagination,
      activeRuleLoading,
      activeTimeRangeRule,
      getRoundingMethodLabel,
      hasTierRates,
      isCurrentlyApplyingRule,
      handleFilterChange,
      handlePageChange,
      handleCreate,
      handleEdit,
      handleEnable,
      handleDisable,
      handleDelete,
      handleViewLogs,
      getStatusBadgeClass,
      getStatusText,
      getTierName,
      formatDateTime,
      fetchRules
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fffbea;
  border: 1px solid #f0c040;
  border-left: 4px solid #f59e0b;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #78350f;
  line-height: 1.6;
}

.info-banner i {
  font-size: 18px;
  color: #f59e0b;
  flex-shrink: 0;
  margin-top: 1px;
}

.info-content .highlight {
  font-weight: 600;
  color: #d97706;
  padding: 1px 5px;
  background: #fef3c7;
  border-radius: 3px;
}

.active-rule-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  color: #666;
  font-size: 14px;
}

.active-rule-card {
  background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%);
  border: 1px solid #86efac;
  border-left: 4px solid #22c55e;
  border-radius: 8px;
  padding: 18px 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.08);
}

.active-rule-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.active-rule-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.active-pulse-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5);
  animation: active-pulse 1.8s ease-in-out infinite;
  flex-shrink: 0;
}

@keyframes active-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(34, 197, 94, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
  }
}

.active-rule-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #166534;
}

.active-rule-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.active-rule-name {
  font-size: 20px;
  font-weight: 700;
  color: #14532d;
  margin-bottom: 6px;
}

.active-rule-period {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 14px;
  color: #15803d;
}

.active-rule-period i {
  font-size: 16px;
}

.period-sep {
  color: #86efac;
}

.active-rule-hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.5;
}

.active-rule-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
  padding-top: 14px;
  border-top: 1px dashed #bbf7d0;
}

.active-detail-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.active-detail-label {
  font-size: 12px;
  font-weight: 600;
  color: #166534;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.active-detail-empty {
  font-size: 13px;
  color: #6b7280;
}

.active-calc-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
}

.calc-mode-tag {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.calc-mode-tag--normal {
  background: #dbeafe;
  color: #1d4ed8;
}

.calc-mode-tag--custom {
  background: #fef3c7;
  color: #b45309;
}

.no-active-rule-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #64748b;
}

.no-active-rule-banner i {
  color: #94a3b8;
  font-size: 18px;
}

.no-active-rule-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
}

.no-active-rule-link:hover {
  text-decoration: underline;
}

.tier-rate-badge--active {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.row-active-rule {
  background: #f0fdf4;
}

.row-active-rule td {
  border-top-color: #bbf7d0;
}

.applying-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 2px 8px;
  background: #22c55e;
  color: #fff;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  vertical-align: middle;
}

.applying-badge i {
  font-size: 12px;
}

.tier-config-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tier-rate-badge {
  display: inline-block;
  padding: 4px 8px;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 4px;
  font-size: 12px;
}

.action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.page-info {
  color: #666;
  font-size: 14px;
}
</style>
